import type { AuthRequest, AuthState, BaseClientInfo } from '@/features/mcp/types';
import { servicesApi } from '@/services/api';
import { isAPIError } from '@/services/api/ts-rest';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { REQUIRED_PARAMS, parseAuthRequest } from './utils';
import { isEmptyString } from '@/utils/string';
import {
  Banner,
  Button,
  Heading,
  Image,
  Link,
  PanelActions,
  PanelFooter,
  PanelSection,
  Paragraph,
  Stack,
} from '@customerio/pluma-components/react';
import { isEmpty, snakeCase } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ErrorView } from './error';
import { AuthLayout } from './layout';
import { SelectWorkspace } from './select-workspace';

export function McpOauth2({ canEditAccount }: { canEditAccount: boolean }) {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<AuthState>({
    isRedirecting: false,
  });
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<Record<string, boolean>>({});
  const mcpFlag = useFeatureFlag(AccountFeatureFlag.McpExternalAccountToggle);
  const aiFlag = useFeatureFlag(AccountFeatureFlag.AiEnabled);
  const isMcpEnabled = mcpFlag && aiFlag;

  useEffect(() => {
    const authRequestParameter = searchParams.get('auth_request');
    if (isEmptyString(authRequestParameter)) {
      setState((previous) => ({
        ...previous,
        error: { type: 'missing_param', param: 'auth_request' },
      }));
      return;
    }

    try {
      const { request, payload, clientInfo } = parseAuthRequest(authRequestParameter);
      const missingParameter = REQUIRED_PARAMS.find((parameter) => {
        const value = request[parameter as keyof AuthRequest];
        return typeof value !== 'string' || isEmptyString(value);
      });
      if (missingParameter != null) {
        setState((previous) => ({
          ...previous,
          error: { type: 'missing_param', param: snakeCase(missingParameter) },
        }));
        return;
      }

      // Validate response type
      if (request.responseType !== 'code') {
        setState((previous) => ({
          ...previous,
          error: {
            type: 'invalid_param',
            param: 'response_type',
            message: 'Only authorization code flow is supported',
          },
        }));
        return;
      }

      setState((previous) => ({
        ...previous,
        clientInfo,
        authRequest: request,
        requestPayload: payload,
        error: undefined,
      }));
    } catch (_error) {
      setState((previous) => ({
        ...previous,
        error: {
          type: 'parse_error',
          message: 'Failed to decode or parse auth_request',
        },
      }));
    }
  }, [searchParams]);

  const handleAllowAccess = async () => {
    if (state.requestPayload == null) {
      setState((previous) => ({
        ...previous,
        error: { type: 'api_error', message: 'No request payload available. Please try again.' },
      }));
      return;
    }

    if (state.error != null) {
      setState((previous) => ({
        ...previous,
        error: { type: 'api_error', message: 'Cannot proceed with authorization due to existing errors.' },
      }));
      return;
    }

    try {
      const body = new URLSearchParams();
      for (const [key, values] of Object.entries(state.requestPayload ?? {})) {
        if (Array.isArray(values)) {
          for (const value of values) {
            if (typeof value === 'string') {
              body.append(key, value);
            }
          }
        }
      }

      for (const [key, value] of Object.entries(selectedWorkspaces)) {
        if (value) {
          body.append('workspace_ids', key);
        }
      }

      const { location } = await servicesApi.oauthMcp({ body: body.toString() });

      setState((previous) => ({ ...previous, isRedirecting: true }));
      window.location.href = location;
    } catch (error: unknown) {
      let message = 'Failed to connect to the authorization server. Please try again.';

      if (isAPIError(error)) {
        // Type guard for error response structure
        const errorBody = error.response.body as { errors?: Array<{ detail?: string }> };
        message = errorBody.errors?.[0]?.detail ?? message;
      }

      setState((previous) => ({
        ...previous,
        error: {
          type: 'api_error',
          message,
        },
      }));
    }
  };

  const isAllowAccessDisabled = useMemo(() => {
    return (
      Object.keys(selectedWorkspaces).length === 0 ||
      Object.values(selectedWorkspaces).every((value) => value === false)
    );
  }, [selectedWorkspaces]);

  if (state.error != null) {
    return <ErrorView error={state.error} />;
  }

  if (state.isRedirecting) {
    return <RedirectView clientName={state.clientInfo?.clientName} />;
  }

  return (
    <AuthLayout>
      <PanelSection>
        <Stack gap="100" alignBlock="center" alignInline="center" mb="300">
          {(() => {
            const logoUri = state.clientInfo?.logoUri;
            const clientName = state.clientInfo?.clientName ?? 'this app';

            return (
              <>
                {!isEmptyString(logoUri) ? (
                  <Image src={logoUri} alt={clientName} size="icon-lg" style={{ borderRadius: '20%' }} />
                ) : null}
                <Heading level="3">Connect with {clientName}</Heading>
              </>
            );
          })()}
        </Stack>

        {!isMcpEnabled ? (
          <McpNotEnabledBanner canEditAccount={canEditAccount} />
        ) : (
          <SelectWorkspace
            selectedWorkspaces={selectedWorkspaces}
            onSelectWorkspaces={(workspaces) => {
              setSelectedWorkspaces(workspaces);
            }}
          />
        )}
      </PanelSection>

      <PanelSection withBorder>
        <PrivacyNotice clientInfo={state.clientInfo} />
      </PanelSection>

      <PanelFooter withBorder>
        <PanelActions>
          {!isMcpEnabled ? (
            <Button variant="primary" href={canEditAccount ? '/settings/privacy' : '/'}>
              {canEditAccount ? 'Go to Privacy, Data & AI Settings' : 'Return to Customer.io'}
            </Button>
          ) : (
            <>
              <Button variant="secondary" href="/">
                Cancel
              </Button>
              <Button variant="primary" onClick={handleAllowAccess} isDisabled={isAllowAccessDisabled}>
                Allow access
              </Button>
            </>
          )}
        </PanelActions>
      </PanelFooter>
    </AuthLayout>
  );
}

interface RedirectViewProps {
  clientName?: string;
}

export const RedirectView = ({ clientName }: RedirectViewProps) => (
  <AuthLayout>
    <PanelSection>
      <Stack gap="200" alignBlock="center" alignInline="center">
        <Heading level="3">Taking you back to {clientName ?? 'the app'}…</Heading>
        <Banner variant="information" title="Please wait">
          If the redirect doesn't happen automatically, you can close this window and return to{' '}
          {clientName ?? 'the app'}.
        </Banner>
      </Stack>
    </PanelSection>
  </AuthLayout>
);

interface PrivacyNoticeProps {
  clientInfo?: BaseClientInfo;
}

export const PrivacyNotice = ({ clientInfo }: PrivacyNoticeProps) => {
  const { clientName, clientUri, tosUri, policyUri } = clientInfo ?? {};
  const displayName = isEmpty(clientName) ? 'this app' : clientName;
  const displayUri = isEmpty(clientUri) ? '' : clientUri?.replace(/^https?:\/\//, '') ?? '';
  const integrationText = isEmpty(clientName) ? '' : ` such as "${clientName}"`;
  const integrationName = isEmpty(clientName) ? 'this integration' : `"${clientName}"`;
  const hasPrivacyAndTerms = !isEmpty(tosUri) && !isEmpty(policyUri);

  return (
    <>
      <Paragraph variant="active" size="xs">
        Make sure you trust {displayName}
        {displayUri.length > 0 ? ` (${displayUri})` : ''}
      </Paragraph>
      <Paragraph size="xs">
        If you continue, you may be sharing sensitive information. Customer.io does not review third-party integrations
        {integrationText}. Learn how {integrationName} handles your data by{' '}
        {hasPrivacyAndTerms ? (
          <>
            reviewing their{' '}
            <Link href={tosUri} isExternal>
              Terms of Service
            </Link>
            {' and '}
            <Link href={policyUri} isExternal>
              Privacy Policy
            </Link>
          </>
        ) : (
          'checking their Privacy Policy and Terms of Service if available'
        )}
        .
      </Paragraph>
    </>
  );
};

interface McpNotEnabledBannerProps {
  canEditAccount: boolean;
}

const McpNotEnabledBanner = ({ canEditAccount }: McpNotEnabledBannerProps) => (
  <Banner variant="warning" title="MCP not enabled">
    {canEditAccount ? (
      <>
        MCP support is not enabled for your account. To use our MCP server, please go to{' '}
        <Link href="/settings/privacy" isUnderlined>
          Privacy, Data & AI Settings
        </Link>{' '}
        and enable Customer.io MCP.
      </>
    ) : (
      <>
        MCP support is not enabled for your account. Please ask an account admin to enable it in Privacy, Data & AI
        Settings.{' '}
        <Link href="https://docs.customer.io/ai/mcp-server/#enable-mcp-support-for-your-account">Learn more</Link>
      </>
    )}
  </Banner>
);
